import { ActionContext } from 'vuex';
import { FormattedProduct } from '@fc/app-common/src/services/Shopify/types';

/**
 * Refreshes product prices for items currently in the cart when country changes.
 *
 * Process:
 * 1. Gets product IDs from items currently in the cart
 * 2. Fetches the entire collection with new country pricing
 *    (Shopify doesn't support fetching by specific product IDs)
 * 3. Filters the collection to only products that are in the cart
 * 4. Updates only those filtered products in the store
 *
 * The efficiency gain is in step 4: by updating individual products instead of
 * replacing the entire productsCollection array, we prevent unnecessary re-renders
 * of components watching the collection.
 */
export async function refreshCartProductPrices(
  { commit, getters }: ActionContext<any, any>,
  country: string,
): Promise<void> {
  try {
    const cart = getters.cart || [];
    if (cart.length === 0) {
      return;
    }

    // Get the product IDs of all cart items
    const productIdsInCart = new Set<string>();
    cart.forEach((cartItem: { id: string }) => {
      const product = getters.getProductVariantById(cartItem.id);
      if (product?.id) {
        productIdsInCart.add(product.id);
      }
    });

    // Fetch the collection with the new country - this gets ALL products with updated pricing
    // This is still necessary since Shopify doesn't have a "fetch by product IDs" endpoint
    // But we only UPDATE the ones in the cart, not the entire collection
    const collectionWithNewPricing = await this.$eCommService.getCollection({
      id: process.env.NUXT_ENV_SHOPIFY_COLLECTION_ID,
      country,
    });

    // Filter to only the products that are in the cart
    const updatedCartProducts = collectionWithNewPricing.filter(
      (product: FormattedProduct) => productIdsInCart.has(product.id),
    );

    if (updatedCartProducts.length === 0) {
      console.warn('⚠️ No matching products found in collection');
      return;
    }

    // Update only the specific products that are in the cart
    // This mutation updates individual products instead of replacing the entire array
    commit('UPDATE_PRODUCTS_IN_COLLECTION', updatedCartProducts);
  } catch (error) {
    console.error('❌ Error refreshing cart product prices:', error);
    throw error;
  }
}
